import { useEffect, useState } from 'react';
import { Table, Button, Modal } from 'antd';
import { ChartFilter } from '../filters/filter-type';
import { api } from 'src/services';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

interface TaskReport {
  patrimony: string;
  description: string;
  sector?: string;
  cell?: string;
  opened: number;
  closed: number;
  dates: { openedAt: string; closedAt?: string }[];
  reasons: string[];
}

interface Props {
  filters: ChartFilter;
}

export default function PatrimoniesReportTable({ filters }: Props) {
  const [data, setData] = useState<TaskReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [modalDates, setModalDates] = useState<string[]>([]);
  const [isDatesModalVisible, setIsDatesModalVisible] = useState(false);

  const [modalReasons, setModalReasons] = useState<string[]>([]);
  const [isReasonsModalVisible, setIsReasonsModalVisible] = useState(false);

  const pageSize = 10;

  async function fetchReport(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...filters,
        page: page.toString(),
        limit: pageSize.toString(),
      } as any).toString();

      const res = await api.get(`/tasks/reports/patrimonies?${params}`);
      setData(res.data.items);
      setTotal(res.data.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching tasks report', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport(1);
  }, [filters]);

  const wrapText = (text: string, length = 70) => {
    const regex = new RegExp(`(.{1,${length}})`, 'g');
    return text.match(regex)?.join('\n') || text;
  };

  const columns = [
    { title: 'Máquina', dataIndex: 'patrimony', key: 'patrimony' },
    { title: 'Descrição', dataIndex: 'description', key: 'description' },
    { title: 'Setor', dataIndex: 'sector', key: 'sector' },
    { title: 'Célula', dataIndex: 'cell', key: 'cell' },
    { title: 'OS Abertas', dataIndex: 'opened', key: 'opened' },
    { title: 'OS Fechadas', dataIndex: 'closed', key: 'closed' },
    {
      title: 'Datas',
      dataIndex: 'dates',
      key: 'dates',
      render: (dates: TaskReport['dates']) => (
        <Button
          type="link"
          onClick={() => {
            const formatted = dates.map(d => {
              const opened = dayjs(d.openedAt).format('DD/MM/YYYY HH:mm');
              const closed = d.closedAt ? dayjs(d.closedAt).format('DD/MM/YYYY HH:mm') : null;
              return closed
                ? `Aberta em: ${opened} - Finalizada em: ${closed}`
                : `Aberta em: ${opened} - Não Finalizada`;
            });
            setModalDates(formatted);
            setIsDatesModalVisible(true);
          }}
        >
          Ver Datas
        </Button>
      ),
    },
    {
      title: 'Descrições',
      dataIndex: 'reasons',
      key: 'reasons',
      render: (reasons: string[]) => {
        return (
          <>
            <Button
              type="link"
              size="small"
              onClick={() => {
                const formatted = reasons.map(r => wrapText(r));
                setModalReasons(formatted);
                setIsReasonsModalVisible(true);
              }}
            >
              Ver Descrições
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="patrimony"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          onChange: (page) => fetchReport(page),
          showSizeChanger: false,
        }}
      />

      <Modal
        title="Datas das OS"
        open={isDatesModalVisible}
        onCancel={() => setIsDatesModalVisible(false)}
        footer={null}
        width="min(520px, calc(100vw - 32px))"
      >
        {modalDates.map((d, idx) => (
          <div key={idx}>{d}</div>
        ))}
      </Modal>

      <Modal
        title="Descrição das OS"
        open={isReasonsModalVisible}
        onCancel={() => setIsReasonsModalVisible(false)}
        footer={null}
        width="min(700px, calc(100vw - 32px))"
      >
        {modalReasons.map((r, idx) => (
          <p
            key={idx}
            style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', marginBottom: 12 }}
          >
            {r}
          </p>
        ))}
      </Modal>
    </>
  );
}
